import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import FullWidthLayout from "@/Shared/Layout/CustomComponents/FullWidthLayout";
import type { UUID } from "@/Shared/types";
import PageLoadingSpinner from "@/Shared/Components/CustomComponents/PageLoadingSpinner";
import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import { useBookerNames } from "@/Hooks/useBookerNames";
import { useOnce } from "@/Hooks/useOnce";
import useQuery from "@/Hooks/useQuery";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QuotesGetInstantQuotesButton } from "@/Shared/Quotes/Buttons/QuotesGetInstantQuotesButton";
import { useShipmentService } from "@/Services/ShipmentService";
import type { FilterShipmentsDescription } from "@/Shared/ShipmentFilter/Dialogs/Internal/FilterShipmentsDialogBody";
import { shipmentFilterRenderDescriptionAsQueryParams } from "@/Shared/ShipmentFilter/Helpers/shipmentFilterRenderDescriptionAsQueryParams";
import { viewShipmentsFilterDescriptionFromQueryParams } from "./Helpers/viewShipmentsFilterDescriptionFromQueryParams";
import { shipmentFilterEmptyDescription } from "@/Shared/ShipmentFilter/Helpers/shipmentFilterEmptyDescription";
import { useShipmentFilterDialog } from "@/Shared/ShipmentFilter/Hooks/useShipmentFilterDialog";
import { SortShipmentOptions } from "@/Shared/Shipment/SortShipmentOptions";
import { viewShipmentsCompareForSort } from "./Helpers/viewShipmentsCompareForSort";
import { viewShipmentsMatchesFilter } from "./Helpers/viewShipmentsMatchesFilter";
import { ViewShipmentsAllShipmentsBody } from "./Internal/viewShipmentsAllShipmentsBody";

function ViewAllShipmentsScreen() {
  const shipmentsService = useShipmentService();
  const { bookerNameFor } = useBookerNames();
  const [bookedShipments, setBookedShipments] = useState<Array<Shipment>>([]);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const navigate = useNavigate();
  const [filter, setFilter] = useState(shipmentFilterEmptyDescription());
  const [sort, setSort] = useState<SortShipmentOptions>(
    SortShipmentOptions.BookingDate,
  );

  async function loadShipments() {
    setLoading(true);
    setLoadFailed(false);
    try {
      const response = await shipmentsService.getShipments();
      setBookedShipments(response.booked);
    } catch (e) {
      console.warn("Error loading shipments:", { e });
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }

  useOnce(async () => {
    await loadShipments();
  });

  const filterShipmentsDialog = useShipmentFilterDialog();
  const query = useQuery();

  useEffect(
    function () {
      const output = viewShipmentsFilterDescriptionFromQueryParams(query);
      setFilter(output.filter);
      setSort(output.sort);
    },

    [window.location.search],
  );

  const noShipments = bookedShipments.length === 0;

  if (loading) {
    return <PageLoadingSpinner />;
  }

  function updateFilter(newFilter: FilterShipmentsDescription) {
    setFilter(newFilter);
    navigate(
      `/view-all-shipments?${shipmentFilterRenderDescriptionAsQueryParams(
        newFilter,
        sort,
      )}`,
    );
  }

  async function onFilter() {
    const filterDescription = await filterShipmentsDialog.show(filter);
    updateFilter(filterDescription);
  }

  function onSortChange(selectedSortOption: SortShipmentOptions) {
    setSort(selectedSortOption);
    navigate(
      `/view-all-shipments?${shipmentFilterRenderDescriptionAsQueryParams(
        filter,
        selectedSortOption,
      )}`,
    );
  }

  function onClick(shipmentId: UUID) {
    const url = `/view-shipment?shipmentId=${shipmentId}`;
    navigate(url);
  }

  function filterShipments() {
    return bookedShipments
      .filter((shipment) => viewShipmentsMatchesFilter(shipment, filter))
      .sort((a, b) => viewShipmentsCompareForSort(a, b, sort));
  }

  const filteredShipments = filterShipments();

  return (
    <>
      {filterShipmentsDialog.dialogs}
      <FullWidthLayout
        header="View All Shipments"
        microcopy={
          noShipments
            ? ""
            : "Access all of your shipments here. Select a filter to dive deeper into your data"
        }
        loading={loading}
        showEmptyState={noShipments}
        onRetryFailedLoad={loadFailed ? loadShipments : undefined}
        emptyState={
          <Stack align="center" gap={8}>
            <Stack align="center" gap={0}>
              {/* Replica of the legacy Cargo Heading1 (28px bold). */}
              <Heading level={1}>You don&apos;t have any shipments yet</Heading>
              <T size="base" color="secondary">
                Once you start running quotes and booking shipments, they will
                be displayed here
              </T>
            </Stack>
            <QuotesGetInstantQuotesButton />
          </Stack>
        }
        content={
          <ViewShipmentsAllShipmentsBody
            onSortChange={onSortChange}
            onFilter={onFilter}
            filter={filter}
            sort={sort}
            onClick={onClick}
            setFilter={updateFilter}
            filteredShipments={filteredShipments}
            bookerNameFor={bookerNameFor}
          />
        }
      />
    </>
  );
}

export default ViewAllShipmentsScreen;
